import { useState, useEffect, useRef } from "react";
import { Dropdown, Image as AntImage, Spin, message } from "antd";
import type { MenuProps } from "antd";
import { Trans } from "react-i18next";
import { t } from "i18next";
import { CommonButton } from "@components/CommonButton";
import Image from "@components/Image";
import {
  deleteFromTrash,
  emptyTrash,
  getTrashAssets,
  restoreFromTrash,
  type UserAssetTrashItemRecord,
} from "@apis/http/api";
import { UserAssetTrashItemResponseAssetType } from "@apis/generated/openapi";
import { IconDelete, IconClose, IconMore } from "@icons/index";
import { createPortal } from "react-dom";
import { useAppDispatch, useAppSelector } from "@hooks/redux";
import { hideTrashDisplay } from "@pages/library/librarySlice";
import { notifyLibraryImageAssetsChanged } from "@pages/library/events";
import { notifyGeneratedVideoAssetsChanged } from "@pages/library/hooks/useGeneratedVideoAssets";
import VideoCover from "@layouts/VideoCover";
import { useKeyup } from "@hooks/utils";
import NoResult from "@components/Section/layouts/NoResult";
import { TrashImageCard } from "./TrashImageCard";

const TrashCard = ({
  record,
  onRestore,
  onDelete,
}: {
  record: UserAssetTrashItemRecord;
  onRestore: (id: string | number) => void;
  onDelete: (id: string | number) => void;
}) => {
  const menuItems: MenuProps["items"] = [
    {
      key: "restore",
      label: (
        <div
          className="w-full h-full flex items-center"
          onClick={(e) => {
            e.stopPropagation();
            onRestore(record.id);
          }}
        >
          <Trans>Restore</Trans>
        </div>
      ),
    },
    {
      key: "delete",
      label: (
        <div
          className="w-full h-full flex items-center text-kds-danger-fg"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(record.id);
          }}
        >
          <Trans>Delete forever</Trans>
        </div>
      ),
    },
  ];

  return (
    <div className="kalo-white-card flex flex-col w-full group relative p-0! overflow-hidden cursor-pointer">
      <div className="relative w-full aspect-3/4">
        <VideoCover
          cover={record.coverImageUrl}
          className="w-full aspect-3/4 rounded-0! border-none! rounded-none!"
          videoUrl={record.url}
          showAdIcon={false}
          videoIconSize="size48"
          adIconSize="size24"
          videoType="video"
          hideDownload
        />
      </div>

      <div className="p-[16px]">
        <div className="flex items-start gap-[8px]">
          {record.productInfo?.productCoverImageUrl ? (
            <Image className="w-[32px] h-[32px] shrink-0 rounded-[4px]" url={record.productInfo.productCoverImageUrl} />
          ) : (
            <div className="w-[32px] h-[32px] shrink-0 rounded-[4px] bg-kds-bg-soft border border-kds-border" />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-[13px] text-kds-fg leading-[18px] line-clamp-2 m-0">
              {record.productInfo?.productTitle || "-"}
            </p>
          </div>
        </div>
      </div>

      <Dropdown trigger={["hover"]} menu={{ items: menuItems }} placement="bottomLeft">
        <div
          className="absolute right-[11px] top-[11px] w-[24px] h-[24px] gap-[3px] cursor-pointer hidden group-hover:flex"
          onClick={(e) => e.stopPropagation()}
        >
          <IconMore className="w-[24px] h-[24px]" />
        </div>
      </Dropdown>
    </div>
  );
};

const GlobalTrashModal = () => {
  const dispatch = useAppDispatch();
  const { trashDisplay } = useAppSelector((state) => state.libraryReducer);
  const [trashList, setTrashList] = useState<UserAssetTrashItemRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [emptyLoading, setEmptyLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewSrc, setPreviewSrc] = useState("");
  const previewOpenRef = useRef(false);
  const ignoreNextEscapeCloseRef = useRef(false);

  if (trashDisplay) {
    document.body.style.overflow = "hidden";
  } else {
    document.body.style.overflow = "overlay";
  }

  const handleClose = () => {
    dispatch(hideTrashDisplay());
    notifyGeneratedVideoAssetsChanged();
    notifyLibraryImageAssetsChanged();
  };

  const handlePreview = (src: string) => {
    if (!src) return;
    setPreviewSrc(src);
    setPreviewOpen(true);
    previewOpenRef.current = true;
  };

  const handlePreviewVisibleChange = (visible: boolean) => {
    setPreviewOpen(visible);
    previewOpenRef.current = visible;
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && previewOpenRef.current) {
        ignoreNextEscapeCloseRef.current = true;
      }
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
    };
  }, []);

  useKeyup(() => {
    if (previewOpenRef.current || ignoreNextEscapeCloseRef.current) {
      ignoreNextEscapeCloseRef.current = false;
      return;
    }
    handleClose();
  });

  const loadTrashList = async () => {
    setLoading(true);
    try {
      const result = await getTrashAssets();
      setTrashList(result ?? []);
    } catch (error) {
      console.error("Failed to fetch trash list:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (trashDisplay) {
      loadTrashList();
    }
  }, [trashDisplay]);

  const handleEmptyTrash = async () => {
    setEmptyLoading(true);
    try {
      await emptyTrash();
      setTrashList([]);
      notifyGeneratedVideoAssetsChanged();
      notifyLibraryImageAssetsChanged();
    } catch (error) {
      console.error("Failed to empty trash:", error);
    } finally {
      setEmptyLoading(false);
    }
  };

  const notifyTrashAssetChanged = (assetType?: string) => {
    if (assetType === UserAssetTrashItemResponseAssetType.IMG) {
      notifyLibraryImageAssetsChanged();
    } else if (assetType === UserAssetTrashItemResponseAssetType.VID) {
      notifyGeneratedVideoAssetsChanged();
    }
  };

  const handleRestore = async (rawId: string | number, assetType?: string) => {
    const id = String(rawId);
    try {
      await restoreFromTrash({ id } as any);
      setTrashList((prev) => prev.filter((item) => String(item.id) !== id));
      notifyTrashAssetChanged(assetType);
    } catch (error) {
      console.error("Failed to restore item:", error);
    }
  };

  const handleDelete = async (rawId: string | number, assetType?: string) => {
    const id = String(rawId);
    try {
      await deleteFromTrash({ id } as any);
      setTrashList((prev) => prev.filter((item) => String(item.id) !== id));
      notifyTrashAssetChanged(assetType);
    } catch (error) {
      console.error("Failed to delete item:", error);
      const backendMessage =
        (error as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message ||
        (error as { message?: string })?.message;
      message.error(backendMessage ? t(backendMessage) : t("Failed"));
    }
  };

  if (trashDisplay) {
    return createPortal(
      <div className="fixed inset-0 z-1000 bg-kds-bg-soft">
        <div className="absolute inset-0 overflow-hidden bg-kds-bg-layout">
          <div className="flex items-center gap-[16px] border-b border-kds-border px-[32px] py-[12px]">
            <CommonButton className="h-[32px] w-[32px] p-0!" onClick={handleClose}>
              <IconClose className="h-[24px]! w-[24px]!" />
            </CommonButton>
            <span className="text-[16px] font-semibold text-kds-fg">
              <Trans>Trash</Trans>
            </span>
          </div>

          <div className="px-[32px] py-[16px]">
            <div className="mb-[12px] flex items-center justify-between">
              <span className="text-[14px] font-normal text-kds-fg-subtle">
                <Trans
                  components={{ span: <span className="text-kds-fg" /> }}
                  defaults="Items are permanently deleted <span>30 days</span> after being moved to the Trash."
                />
              </span>
              <CommonButton onClick={handleEmptyTrash} loading={emptyLoading} disabled={trashList.length === 0}>
                <IconDelete className="h-[16px] w-[16px] [&_path]:stroke-current" />
                <Trans>Empty Trash</Trans>
              </CommonButton>
            </div>

            <Spin spinning={loading}>
              <div className="scrollbar-none h-[calc(100vh-150px)] overflow-y-auto">
                {trashList.length > 0 ? (
                  <div className="grid grid-cols-5 gap-[16px] min-[1920px]:grid-cols-6">
                    {trashList.map((item) =>
                      item.assetType === UserAssetTrashItemResponseAssetType.IMG ? (
                        <TrashImageCard
                          key={item.id}
                          record={item}
                          onPreview={handlePreview}
                          onRestore={(id) => handleRestore(id, item.assetType)}
                          onDelete={(id) => handleDelete(id, item.assetType)}
                        />
                      ) : (
                        <TrashCard
                          key={item.id}
                          record={item}
                          onRestore={(id) => handleRestore(id, item.assetType)}
                          onDelete={(id) => handleDelete(id, item.assetType)}
                        />
                      ),
                    )}
                  </div>
                ) : (
                  <div className="flex h-[400px] items-center justify-center">
                    <NoResult blankTip="Trash is empty" />
                  </div>
                )}
              </div>
            </Spin>
          </div>
        </div>

        <AntImage
          src={previewSrc}
          styles={{ root: { display: "none" } }}
          preview={{
            open: previewOpen,
            onOpenChange: handlePreviewVisibleChange,
          }}
        />
      </div>,
      document.body,
    );
  }

  return null;
};

export default GlobalTrashModal;
